import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Bird, Plant, PawPrint, Bug, FirstAidKit, Tree, Flower, Footprints,
  Cat, Fish, Butterfly, Mountains, Tag
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { API } from "@/config/constants";

const suggestedTags = [
  { name: "birds", icon: Bird, color: "#C25A3C" },
  { name: "plants", icon: Plant, color: "#6B8E23" },
  { name: "wildlife", icon: PawPrint, color: "#55624F" },
  { name: "insects", icon: Bug, color: "#D99559" },
  { name: "fungi", icon: FirstAidKit, color: "#8B4513" },
  { name: "trees", icon: Tree, color: "#228B22" },
  { name: "wildflowers", icon: Flower, color: "#DB7093" },
  { name: "reptiles", icon: Footprints, color: "#8B4513" },
  { name: "mammals", icon: Cat, color: "#55624F" },
  { name: "fish", icon: Fish, color: "#4682B4" },
  { name: "butterflies", icon: Butterfly, color: "#DA70D6" },
  { name: "hiking", icon: Mountains, color: "#7C8068" },
];

const TagsPage = () => {
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await axios.get(`${API}/tags`);
        setTags(response.data);
      } catch (error) {
        toast.error("Failed to load tags");
      } finally {
        setLoading(false);
      }
    };
    fetchTags();
  }, []);

  const existingTagNames = tags.map(t => t.name);
  const unusedSuggested = suggestedTags.filter(st => !existingTagNames.includes(st.name));

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="tags-page">
      <h1 className="font-heading text-3xl font-semibold text-[#2E332B] mb-2">Tags</h1>
      <p className="text-[#7C8068] mb-6">Browse entries by category or explore suggested nature tags</p>

      {loading ? (
        <div className="flex flex-wrap gap-3">
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} className="h-10 w-24 bg-[#EAE7DE] rounded-full" />
          ))}
        </div>
      ) : (
        <>
          {tags.length > 0 && (
            <div className="mb-8">
              <h2 className="font-heading text-lg font-semibold text-[#2E332B] mb-3">Your Tags</h2>
              <div className="flex flex-wrap gap-3">
                {tags.map(tag => {
                  const suggested = suggestedTags.find(st => st.name === tag.name);
                  const Icon = suggested ? suggested.icon : Tag;
                  return (
                    <Button
                      key={tag.name}
                      variant="outline"
                      onClick={() => navigate(`/?tag=${encodeURIComponent(tag.name)}`)}
                      className="rounded-full border-[#D2C2B3] text-[#2E332B] hover:bg-[#EAE7DE] hover:border-[#55624F]"
                      data-testid={`tag-${tag.name}`}
                    >
                      <Icon size={16} className="mr-2" weight="duotone" />
                      {tag.name}
                      <span className="ml-2 text-[#7C8068]">({tag.count})</span>
                    </Button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <h2 className="font-heading text-lg font-semibold text-[#2E332B] mb-3">
              {tags.length > 0 ? "Explore More Categories" : "Nature Categories"}
            </h2>
            <p className="text-sm text-[#7C8068] mb-3">Start a new entry with one of these tags</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {(tags.length > 0 ? unusedSuggested : suggestedTags).map(st => {
                const Icon = st.icon;
                const existing = tags.find(t => t.name === st.name);
                return (
                  <Card
                    key={st.name}
                    className="cursor-pointer border-[#D2C2B3] hover:border-[#55624F] transition-colors card-hover"
                    onClick={() => existing
                      ? navigate(`/?tag=${encodeURIComponent(st.name)}`)
                      : navigate(`/entry/new`)
                    }
                    data-testid={`suggested-tag-${st.name}`}
                  >
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: st.color + "18" }}>
                        <Icon size={22} weight="duotone" style={{ color: st.color }} />
                      </div>
                      <div>
                        <span className="font-medium text-[#2E332B] capitalize">{st.name}</span>
                        {existing && (
                          <p className="text-xs text-[#7C8068]">{existing.count} entries</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TagsPage;
